import { cx } from "@unizpay/ui";
import { Menu as MenuIcon, Search } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router";
import { useMode } from "../lib/mode";
import { whenSafeToLeave } from "../lib/unsavedChanges";

/** Searches payments by UnizPay id or the merchant's payment_id. */
/** An EVM hash or address, or a Solana / TRON one (base58): what Transactions searches. */
const ON_CHAIN = /^(0x[0-9a-fA-F]{40}|0x[0-9a-fA-F]{64}|[1-9A-HJ-NP-Za-km-z]{32,88})$/;

function SearchBox() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const value = q.trim();
    // Hashes and addresses are found on Transactions; payment IDs (and the rest) on Payments.
    const page = ON_CHAIN.test(value) ? "/transactions" : "/payments";
    navigate(value ? `${page}?q=${encodeURIComponent(value)}` : "/payments");
    setQ("");
  }

  return (
    <form onSubmit={onSubmit} className="relative min-w-0 flex-1 sm:max-w-sm">
      <label htmlFor="global-search" className="sr-only">
        Search payments, transaction hashes and addresses
      </label>
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-fg-subtle"
      />
      <input
        id="global-search"
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Payment ID, tx hash or address"
        className="h-8 w-full rounded-md border border-transparent bg-subtle pr-2.5 pl-8 text-sm placeholder:text-fg-subtle focus:border-ring focus:bg-surface focus:outline-2 focus:outline-offset-0 focus:outline-ring/30"
      />
    </form>
  );
}

function ModeSwitch() {
  const { mode, setMode } = useMode();
  const on = mode === "test";
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => whenSafeToLeave(() => setMode(on ? "live" : "test"))}
      className="flex shrink-0 items-center gap-2 rounded-md px-1.5 py-1 text-[0.8125rem] font-medium text-fg-muted hover:text-fg"
    >
      <span className="hidden sm:inline">Test mode</span>
      <span className="sm:hidden">Test</span>
      <span
        aria-hidden="true"
        className={cx(
          "relative inline-flex h-4 w-7 items-center rounded-full transition-colors",
          on ? "bg-test" : "bg-line-strong",
        )}
      >
        <span
          className={cx(
            "absolute size-3 rounded-full bg-white shadow transition-transform",
            on ? "translate-x-3.5" : "translate-x-0.5",
          )}
        />
      </span>
    </button>
  );
}

export function TopBar({ onOpenMenu, className }: { onOpenMenu: () => void; className?: string }) {
  const { testMode } = useMode();
  return (
    <header
      className={cx(
        "sticky z-20 flex h-14 items-center gap-2 border-b border-line bg-surface px-4 sm:gap-3 sm:px-6 lg:px-8",
        className,
      )}
    >
      <button
        type="button"
        onClick={onOpenMenu}
        className="-ml-1.5 rounded-md p-1.5 text-fg-muted hover:bg-subtle hover:text-fg lg:hidden"
      >
        <MenuIcon aria-hidden="true" className="size-5" />
        <span className="sr-only">Open navigation</span>
      </button>
      <SearchBox />
      <div className="ml-auto flex items-center gap-2">{testMode && <ModeSwitch />}</div>
    </header>
  );
}
